import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { authFetch, useAuth } from '../auth/AuthContext';

/*
  LenderEmailDrafts
  Per-lender draft review surface, opened from the Lender Match tab.

  Props:
    dealId        deal record id
    driveLink     deal Drive folder URL (string) or '' if none. Controls whether
                  the docs-link toggle appears at all.
    lenders       array of { id, name, aeName, email, submissionEmail, aeEmail,
                  lenderEmail } already filtered to the user's selection in the
                  match list. No re-judging here. email is the resolved default
                  (submission, then AE, then lender); the individual fields let
                  the user see and switch the To address.
    onClose       called when the user closes the surface.

  Behavior:
    - One draft per lender, built server-side by assemble-lender-email.
    - The body is HTML. The preview is a live formatted editor (contenteditable),
      so bold section titles and indented bullets render as they will in the email.
    - Per-lender disclosure (full | blind), defaulting to full. Blind keeps the
      Deal Summary but the engine scrubs identifying details.
    - Per-lender docs link (off by default). The toggle adds or removes the
      Documents link from the body. It only renders when the deal has a Drive folder.
    - A sticky header keeps the current lender name, AE, and To address visible
      while scrolling the draft.
    - Copy puts rich HTML on the clipboard so a paste into Gmail keeps the
      formatting. Mark as emailed is a local checkmark for now (Send writes to
      Airtable in the next build).
*/

const st = {
  wrap:{marginTop:'4px'},
  bar:{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap',marginBottom:'12px'},
  barLeft:{display:'flex',alignItems:'center',gap:'10px'},
  barTitle:{fontSize:'13px',fontWeight:600,color:'#185FA5',display:'flex',alignItems:'center',gap:'5px'},
  barCount:{fontSize:'12px',color:'#8a9ab5'},
  closeBtn:{fontSize:'12px',padding:'5px 12px',background:'transparent',color:'#8a9ab5',border:'1.5px solid #B8D4F0',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontWeight:600},
  grid:{display:'grid',gridTemplateColumns:'220px 1fr',gap:'12px',alignItems:'start'},
  list:{display:'flex',flexDirection:'column',gap:'6px',position:'sticky',top:'8px'},
  row:(active)=>({cursor:'pointer',border:'1px solid '+(active?'#2B72C2':'#D6E2F2'),borderLeft:'4px solid '+(active?'#2B72C2':'#D6E2F2'),background:active?'#EAF1FA':'#fff',borderRadius:'8px',padding:'9px 10px'}),
  rowTop:{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'6px'},
  rowName:{fontSize:'12px',fontWeight:600,color:'#0D2545',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  rowAe:{fontSize:'11px',color:'#8a9ab5',marginTop:'2px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'},
  rowEditing:{fontSize:'10px',fontWeight:700,color:'#2B72C2',marginTop:'3px',display:'flex',alignItems:'center',gap:'3px'},
  badge:(kind)=>{
    const map={full:{bg:'#E4F2DA',fg:'#2D5E0E'},blind:{bg:'#FAEEDA',fg:'#7A4A08'},sent:{bg:'#EAF1FA',fg:'#5a6b85'}};
    const c=map[kind]||map.full;
    return {fontSize:'10px',fontWeight:700,padding:'1px 7px',borderRadius:'5px',background:c.bg,color:c.fg,whiteSpace:'nowrap'};
  },
  panel:{border:'0.5px solid #D6E2F2',borderRadius:'10px',background:'#fff',overflow:'hidden'},
  stickyHead:{position:'sticky',top:'0',zIndex:5,background:'#F5F9FE',borderBottom:'1px solid #D6E2F2',padding:'10px 14px'},
  headTop:{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'},
  headName:{fontSize:'15px',fontWeight:700,color:'#0D3565',display:'flex',alignItems:'center',gap:'7px'},
  headMeta:{fontSize:'11.5px',color:'#5a6b85',marginTop:'2px'},
  toVal:{color:'#185FA5',fontWeight:600},
  toMissing:{color:'#B07A1A',fontStyle:'italic'},
  toLabel:{color:'#8a9ab5'},
  toSelect:{fontSize:'11.5px',padding:'2px 6px',border:'1.5px solid #B8D4F0',borderRadius:'5px',color:'#185FA5',fontFamily:'inherit',background:'#fff',fontWeight:600,maxWidth:'320px'},
  toInput:{fontSize:'11.5px',padding:'3px 8px',border:'1.5px solid #B8D4F0',borderRadius:'5px',color:'#185FA5',fontFamily:'inherit',background:'#fff',fontWeight:600,minWidth:'240px',verticalAlign:'middle'},
  toChips:{display:'inline-flex',gap:'4px',marginLeft:'7px',verticalAlign:'middle'},
  toChip:(on)=>({fontSize:'10px',fontWeight:700,padding:'2px 8px',borderRadius:'5px',cursor:'pointer',fontFamily:'inherit',border:'1.5px solid '+(on?'#7AADE0':'#D6E2F2'),background:on?'#EAF1FA':'#fff',color:on?'#185FA5':'#8a9ab5'}),
  ctrls:{display:'flex',gap:'6px',alignItems:'center',flexWrap:'wrap',marginTop:'8px'},
  segBtn:(active,kind)=>{
    const on = kind==='blind'
      ? {bg:'#FAEEDA',fg:'#7A4A08',bd:'#E8C07A'}
      : {bg:'#EAF1FA',fg:'#185FA5',bd:'#7AADE0'};
    return {fontSize:'11px',padding:'4px 10px',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontWeight:600,
      background:active?on.bg:'#fff',color:active?on.fg:'#5a6b85',border:'1.5px solid '+(active?on.bd:'#D6E2F2')};
  },
  sep:{width:'1px',height:'18px',background:'#D6E2F2',margin:'0 2px'},
  docsBtn:(on)=>({fontSize:'11px',padding:'4px 10px',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontWeight:600,
    display:'inline-flex',alignItems:'center',gap:'3px',
    background:on?'#EAF1FA':'#fff',color:on?'#185FA5':'#5a6b85',border:'1.5px solid '+(on?'#7AADE0':'#D6E2F2')}),
  pad:{padding:'12px 14px'},
  subjLine:{fontSize:'12px',color:'#8a9ab5',marginBottom:'8px'},
  subjVal:{color:'#0D2545'},
  editor:{width:'100%',boxSizing:'border-box',minHeight:'340px',padding:'14px 16px',border:'1.5px solid #B8D4F0',borderRadius:'7px',
    fontFamily:'Arial, Helvetica, sans-serif',fontSize:'14px',lineHeight:1.5,color:'#0D2545',outline:'none',background:'#fff',overflowY:'auto'},
  actions:{display:'flex',gap:'8px',marginTop:'12px',flexWrap:'wrap',alignItems:'center'},
  copyBtn:{fontSize:'12px',padding:'6px 14px',background:'#2B72C2',color:'#fff',border:'none',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontWeight:600,display:'inline-flex',alignItems:'center',gap:'5px'},
  sendBtn:(sent)=>({fontSize:'12px',padding:'7px 16px',background:sent?'#2D5E0E':'#154D97',color:'#fff',border:'none',borderRadius:'6px',cursor:sent?'default':'pointer',fontFamily:'inherit',fontWeight:700,display:'inline-flex',alignItems:'center',gap:'5px'}),
  markBtn:(on)=>({fontSize:'12px',padding:'6px 14px',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontWeight:600,display:'inline-flex',alignItems:'center',gap:'5px',
    background:on?'#E4F2DA':'#fff',color:on?'#2D5E0E':'#5a6b85',border:'1.5px solid '+(on?'#A9D08A':'#D6E2F2')}),
  editedTag:{fontSize:'11px',color:'#8a9ab5',display:'inline-flex',alignItems:'center',gap:'3px'},
  loading:{fontSize:'12px',color:'#8a9ab5',padding:'30px 0',textAlign:'center'},
  err:{fontSize:'11.5px',color:'#c0392b',marginTop:'8px'},
  hint:{fontSize:'11px',color:'#8a9ab5',marginTop:'10px',fontStyle:'italic'},
  empty:{fontSize:'13px',color:'#8a9ab5',padding:'10px 0'},
};

// Build the distinct To options for a lender, in precedence order:
// submission email, then AE email, then lender email. Deduped by address.
function emailCandidates(l) {
  if (!l) return [];
  const seen = new Set();
  const out = [];
  const add = (label, email) => {
    const e = String(email || '').trim();
    if (e && !seen.has(e.toLowerCase())) { seen.add(e.toLowerCase()); out.push({ label, email: e }); }
  };
  add('Submission', l.submissionEmail);
  add('AE', l.aeEmail);
  add('Lender', l.lenderEmail);
  if (out.length === 0) add('Lender', l.email); // fallback to resolved default
  return out;
}

function htmlToPlain(html) {
  let t = String(html || '');
  t = t.replace(/<\/p>/gi, '\n\n');
  t = t.replace(/<li[^>]*>/gi, '\n  - ');
  t = t.replace(/<\/li>/gi, '');
  t = t.replace(/<\/ul>/gi, '\n');
  t = t.replace(/<br\s*\/?>/gi, '\n');
  t = t.replace(/<[^>]+>/g, '');
  t = t.replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
       .replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ');
  t = t.replace(/\n{3,}/g, '\n\n').replace(/[ \t]+\n/g, '\n');
  return t.trim();
}

export default function LenderEmailDrafts({ dealId, driveLink, lenders, onClose }) {
  const safeLenders = Array.isArray(lenders) ? lenders : [];
  const hasDrive = !!(driveLink && String(driveLink).trim());

  const [cur, setCur] = useState(0);
  const [state, setState] = useState(() => {
    const init = {};
    safeLenders.forEach(l => { init[l.id] = { mode: 'full', docs: false, emailed: false, edited: false, html: null, toEmail: null, recording: false, recorded: false, recordErr: '', sending: false, sent: false, sendErr: '' }; });
    return init;
  });
  // Cache of assembled drafts: key `${id}|${mode}|${docs?1:0}` -> { email, subject }
  const cacheRef = useRef({});
  const editorRef = useRef(null);
  const [draft, setDraft] = useState({ email: '', subject: '' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  // Gmail send: a short-lived access token with the gmail.send scope, obtained
  // from the existing Google sign-in. Held in memory with its expiry and reused
  // until it lapses, so the user is not prompted on every send.
  const { user } = useAuth();
  const gmailTokenRef = useRef({ token: null, expiresAt: 0 });
  const pendingSendRef = useRef(null);

  const requestGmailToken = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/gmail.send',
    onSuccess: (resp) => {
      const ttl = (resp.expires_in ? resp.expires_in : 3600) * 1000;
      gmailTokenRef.current = { token: resp.access_token, expiresAt: Date.now() + ttl - 60000 };
      const job = pendingSendRef.current;
      pendingSendRef.current = null;
      if (job) job(resp.access_token);
    },
    onError: () => {
      pendingSendRef.current = null;
      if (lender) patch(lender.id, { sending: false, sendErr: 'Gmail connection was cancelled or failed.' });
    },
  });

  function withGmailToken(run) {
    const t = gmailTokenRef.current;
    if (t.token && t.expiresAt > Date.now()) { run(t.token); return; }
    pendingSendRef.current = run;   // run once the consent popup returns a token
    requestGmailToken();
  }

  const lender = safeLenders[cur] || null;
  const ls = lender ? (state[lender.id] || { mode: 'full', docs: false, emailed: false, edited: false, html: null, toEmail: null }) : null;

  const toCandidates = lender ? emailCandidates(lender) : [];
  // A typed address (ls.toEmail) always wins, even if it is not one on file.
  // Otherwise fall back to the first candidate on file.
  const hasTyped = !!(ls && ls.toEmail != null && ls.toEmail !== '');
  const chosenTo = hasTyped ? ls.toEmail : (toCandidates[0] ? toCandidates[0].email : '');
  const chosenLabel = (toCandidates.find(c => c.email === chosenTo) || {}).label || '';

  const buildKey = useCallback((l, s) => `${l.id}|${s.mode}|${s.docs ? 1 : 0}`, []);

  const fetchDraft = useCallback(async (l, s) => {
    const key = buildKey(l, s);
    if (cacheRef.current[key]) { setDraft(cacheRef.current[key]); setErr(''); return; }
    setLoading(true); setErr('');
    try {
      const res = await authFetch('/.netlify/functions/assemble-lender-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId,
          lenderName: l.name || '',
          aeName: l.aeName || '',
          disclosure: s.mode,
          includeDriveLink: s.docs,
          driveLink: s.docs ? (driveLink || '') : '',
        }),
      });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || 'Failed to build email');
      const built = { email: data.email || '', subject: data.subject || '' };
      cacheRef.current[key] = built;
      setDraft(built);
    } catch (e) {
      setErr('Could not build this draft: ' + e.message);
      setDraft({ email: '', subject: '' });
    } finally {
      setLoading(false);
    }
  }, [dealId, driveLink, buildKey]);

  // Trigger a fetch whenever the selected lender, mode, or docs changes and
  // there is no saved edit for that exact combination.
  useEffect(() => {
    if (!lender || !ls) return;
    if (ls.html != null) return; // edited content is restored by the editor effect below
    fetchDraft(lender, ls);
  }, [cur, lender, ls && ls.mode, ls && ls.docs]); // eslint-disable-line

  // Push HTML into the contenteditable when the source changes for non-typing
  // reasons (lender switch, mode/docs change, fresh draft arrival). Typing does
  // not run this, so the cursor is never reset mid-edit.
  useEffect(() => {
    if (!editorRef.current || !lender || !ls) return;
    const next = ls.html != null ? ls.html : draft.email;
    if (editorRef.current.innerHTML !== next) {
      editorRef.current.innerHTML = next || '';
    }
  }, [cur, lender, ls && ls.html, ls && ls.mode, ls && ls.docs, draft.email]); // eslint-disable-line

  function patch(id, changes) {
    setState(prev => ({ ...prev, [id]: { ...prev[id], ...changes } }));
  }

  function pick(i) { setCopied(false); setCur(i); }
  function setMode(m) {
    if (!lender || !ls || ls.mode === m) return;
    patch(lender.id, { mode: m, html: null, edited: false });
  }
  function toggleDocs() {
    if (!lender || !ls) return;
    patch(lender.id, { docs: !ls.docs, html: null, edited: false });
  }
  function onInput() {
    if (!lender || !editorRef.current) return;
    patch(lender.id, { html: editorRef.current.innerHTML, edited: true });
  }
  async function recordSend(l, s, toAddr) {
    const res = await authFetch('/.netlify/functions/record-lender-send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dealId,
        lenderId: l.id,
        lenderName: l.name || '',
        aeName: l.aeName || '',
        toEmail: toAddr || '',
        disclosure: s.mode,
        docsIncluded: !!s.docs,
      }),
    });
    const data = await res.json();
    if (!res.ok || data.error) throw new Error(data.error || 'Could not record the send');
    return data;
  }

  async function toggleEmailed() {
    if (!lender || !ls) return;
    // Un-marking is local only; we do not delete the recorded row here.
    if (ls.emailed) { patch(lender.id, { emailed: false }); return; }

    // Marking emailed by hand (you sent it yourself): just record it.
    patch(lender.id, { emailed: true, recording: true, recordErr: '' });
    try {
      await recordSend(lender, ls, chosenTo);
      patch(lender.id, { recording: false, recorded: true });
    } catch (e) {
      patch(lender.id, { emailed: false, recording: false, recordErr: e.message });
    }
  }

  // Send the current draft through the user's Gmail, then record it.
  function sendFromGmail() {
    if (!lender || !ls) return;
    if (!chosenTo) { patch(lender.id, { sendErr: 'No recipient address on file for this lender.' }); return; }
    const theLender = lender;
    const theState = ls;
    const toAddr = chosenTo;
    const subject = draft.subject || '';
    const htmlBody = editorRef.current ? editorRef.current.innerHTML
      : ((theState.html != null) ? theState.html : draft.email);

    patch(theLender.id, { sending: true, sendErr: '' });

    withGmailToken(async (accessToken) => {
      try {
        const res = await authFetch('/.netlify/functions/send-gmail', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            accessToken,
            to: toAddr,
            subject,
            html: htmlBody,
            fromEmail: (user && user.email) || '',
          }),
        });
        const data = await res.json();
        if (!res.ok || data.error) throw new Error(data.error || 'Send failed');
        // Sent. Mark emailed and record it (best effort on the record).
        patch(theLender.id, { sending: false, sent: true, emailed: true, recording: true, sendErr: '' });
        try {
          await recordSend(theLender, theState, toAddr);
          patch(theLender.id, { recording: false, recorded: true });
        } catch (e) {
          patch(theLender.id, { recording: false, recordErr: e.message });
        }
      } catch (e) {
        patch(theLender.id, { sending: false, sendErr: e.message });
      }
    });
  }

  async function copy() {
    const html = editorRef.current ? editorRef.current.innerHTML : ((ls && ls.html != null) ? ls.html : draft.email);
    const text = htmlToPlain(html);
    try {
      if (window.ClipboardItem && navigator.clipboard && navigator.clipboard.write) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([text], { type: 'text/plain' }),
          }),
        ]);
      } else if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch (e) {
      try { if (navigator.clipboard) await navigator.clipboard.writeText(text); } catch (e2) { /* ignore */ }
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    }
  }

  if (safeLenders.length === 0) {
    return (
      <div style={st.wrap}>
        <div style={st.bar}>
          <div style={st.barLeft}><span style={st.barTitle}><i className="ti ti-mail" style={{fontSize:'15px'}}/> Email drafts</span></div>
          <button style={st.closeBtn} onClick={onClose}>Close</button>
        </div>
        <div style={st.empty}>No lenders selected. Check lenders in the match list above, then generate.</div>
      </div>
    );
  }

  return (
    <div style={st.wrap}>
      <div style={st.bar}>
        <div style={st.barLeft}>
          <span style={st.barTitle}><i className="ti ti-mail" style={{fontSize:'15px'}}/> Email drafts</span>
          <span style={st.barCount}>{safeLenders.length} lender{safeLenders.length===1?'':'s'}</span>
        </div>
        <button style={st.closeBtn} onClick={onClose}>Close</button>
      </div>

      <div style={st.grid}>
        <div style={st.list}>
          {safeLenders.map((l, i) => {
            const s = state[l.id] || {};
            const active = i === cur;
            const kind = s.emailed ? 'sent' : (s.mode === 'blind' ? 'blind' : 'full');
            const label = s.emailed ? 'Sent' : (s.mode === 'blind' ? 'Blind' : 'Full');
            return (
              <div key={l.id} style={st.row(active)} onClick={() => pick(i)}>
                <div style={st.rowTop}>
                  <span style={st.rowName}>{l.name || 'Unnamed lender'}</span>
                  <span style={st.badge(kind)}>{label}</span>
                </div>
                <div style={st.rowAe}>{l.aeName || 'no AE on file'}</div>
                {active && <div style={st.rowEditing}><i className="ti ti-pencil" style={{fontSize:'11px'}}/> editing</div>}
              </div>
            );
          })}
        </div>

        <div style={st.panel}>
          <div style={st.stickyHead}>
            <div style={st.headTop}>
              <div>
                <div style={st.headName}>
                  <i className="ti ti-building-bank" style={{fontSize:'16px',color:'#2B72C2'}}/>
                  {lender ? (lender.name || 'Unnamed lender') : ''}
                </div>
                <div style={st.headMeta}>
                  {lender && lender.aeName ? lender.aeName + '  ·  ' : ''}
                  <span style={{verticalAlign:'middle'}}>To:</span>{' '}
                  <input
                    style={st.toInput}
                    value={chosenTo}
                    placeholder="type recipient email"
                    onClick={e => e.stopPropagation()}
                    onChange={e => lender && patch(lender.id, { toEmail: e.target.value })}
                  />
                  {toCandidates.length > 0 && (
                    <span style={st.toChips}>
                      {toCandidates.map(c => (
                        <button key={c.email} type="button" style={st.toChip(c.email === chosenTo)}
                          onClick={e => { e.stopPropagation(); lender && patch(lender.id, { toEmail: c.email }); }}>
                          {c.label}
                        </button>
                      ))}
                    </span>
                  )}
                </div>
              </div>
              <div style={st.ctrls}>
                <button style={st.segBtn(ls.mode==='full','full')} onClick={() => setMode('full')}>Full</button>
                <button style={st.segBtn(ls.mode==='blind','blind')} onClick={() => setMode('blind')}>Blind</button>
                {hasDrive && <>
                  <span style={st.sep}/>
                  <button style={st.docsBtn(ls.docs)} onClick={toggleDocs}>
                    <i className="ti ti-link" style={{fontSize:'13px'}}/> Docs link: {ls.docs ? 'on' : 'off'}
                  </button>
                </>}
              </div>
            </div>
          </div>

          <div style={st.pad}>
            <div style={st.subjLine}>Subject: <span style={st.subjVal}>{draft.subject || '—'}</span></div>

            {loading && <div style={st.loading}>Building draft…</div>}
            <div
              ref={editorRef}
              style={{ ...st.editor, display: loading ? 'none' : 'block' }}
              contentEditable
              suppressContentEditableWarning
              onInput={onInput}
            />

            <div style={st.actions}>
              <button style={st.sendBtn(ls.sent)} onClick={sendFromGmail} disabled={ls.sending || ls.sent}>
                <i className={`ti ${ls.sending?'ti-loader-2':(ls.sent?'ti-circle-check':'ti-send')}`} style={{fontSize:'14px'}}/> {ls.sending ? 'Sending...' : (ls.sent ? 'Sent from Gmail' : 'Send from Gmail')}
              </button>
              <button style={st.copyBtn} onClick={copy}>
                <i className={`ti ${copied?'ti-check':'ti-copy'}`} style={{fontSize:'14px'}}/> {copied ? 'Copied (formatted)' : 'Copy instead'}
              </button>
              {!ls.sent && <button style={st.markBtn(ls.emailed)} onClick={toggleEmailed} disabled={ls.recording}>
                <i className={`ti ${ls.recording?'ti-loader-2':'ti-check'}`} style={{fontSize:'14px'}}/> {ls.recording ? 'Recording...' : (ls.emailed ? 'Emailed' : 'Mark as emailed')}
              </button>}
              {ls.recorded && !ls.recording && <span style={st.editedTag}><i className="ti ti-database" style={{fontSize:'12px'}}/> logged to deal</span>}
              {ls.edited && <span style={st.editedTag}><i className="ti ti-pencil" style={{fontSize:'12px'}}/> edited</span>}
            </div>
            {ls.sendErr && <div style={st.err}>Could not send: {ls.sendErr}</div>}
            {ls.recordErr && <div style={st.err}>Sent, but could not log it to the deal: {ls.recordErr}</div>}

            {err && <div style={st.err}>{err}</div>}
            <div style={st.hint}>Set Full or Blind and the docs link first, then edit. Switching either rebuilds the draft and clears edits. Copy keeps the bold headings and bullets when you paste into Gmail.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
